import { useState, useEffect } from "react";
import { Link } from "react-router-dom";

const navItems = [
  {
    label: "Products",
    items: [
      { label: "Payments", href: "/products/payments" },
      { label: "Subscriptions", href: "/products/subscriptions" },
      { label: "Connect", href: "/products/connect" },
      { label: "Radar", href: "/products/radar" },
      { label: "Billing", href: "/products/billing" },
      { label: "Terminal", href: "/products/terminal" },
    ],
  },
  {
    label: "Developers",
    items: [
      { label: "Documentation", href: "/developers/docs" },
      { label: "SDKs & Libraries", href: "/developers/sdk" },
      { label: "Status & Webhooks", href: "/developers/webhooks" },
    ],
  },
  { label: "Pricing", href: "/pricing" },
  {
    label: "Resources",
    items: [
      { label: "Case Studies", href: "/resources/case-studies" },
      { label: "Guides", href: "/resources/guides" },
      { label: "Partners", href: "/resources/partners" },
    ],
  },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-white/95 backdrop-blur-md border-b border-gray-100" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8 flex items-center justify-between h-[70px]">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2 flex-shrink-0">
          <img
            src="https://static.readdy.ai/image/37f481326fda0879b2d66b8287a2bda5/2dc7da0ef423b61930fc253761bea490.png"
            alt="Paygent Logo"
            className="h-56 w-auto"
          />
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-1">
          {navItems.map((item) => (
            <div
              key={item.label}
              className="relative"
              onMouseEnter={() => item.items && setOpenMenu(item.label)}
              onMouseLeave={() => setOpenMenu(null)}
            >
              {item.href ? (
                <Link to={item.href} className="flex items-center gap-1 px-3 py-2 text-[15px] font-medium text-gray-700 hover:text-blue-600 rounded-lg hover:bg-blue-50/60 transition-all duration-200 whitespace-nowrap cursor-pointer">
                  {item.label}
                </Link>
              ) : (
                <button className="flex items-center gap-1 px-3 py-2 text-[15px] font-medium text-gray-700 hover:text-blue-600 rounded-lg hover:bg-blue-50/60 transition-all duration-200 whitespace-nowrap cursor-pointer">
                  {item.label}
                  {item.items && (
                    <i className="ri-arrow-down-s-line text-sm w-4 h-4 flex items-center justify-center opacity-60" />
                  )}
                </button>
              )}
              {item.items && openMenu === item.label && (
                <div className="absolute top-full left-0 mt-1 bg-white rounded-xl border border-gray-100 py-2 min-w-[180px] animate-fade-in"
                  style={{ boxShadow: "0 8px 24px rgba(0,0,0,0.08)" }}
                >
                  {item.items.map((sub) => (
                    <Link
                      key={sub.label}
                      to={sub.href}
                      className="block px-4 py-2 text-sm text-gray-600 hover:text-blue-600 hover:bg-blue-50/60 transition-colors duration-150"
                      onClick={() => setOpenMenu(null)}
                    >
                      {sub.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          ))}
          {/* Tapn branded button */}
          <Link
            to="/tapn"
            className="flex items-center gap-1.5 ml-1 px-3 py-1.5 rounded-lg hover:bg-indigo-50/80 transition-all duration-200 group"
          >
            <img src="/tapn-logo.png" alt="Tapn" className="h-7 w-auto object-contain" style={{ mixBlendMode: 'multiply' }} />
          </Link>
        </nav>

        {/* CTA buttons */}
        <div className="hidden md:flex items-center gap-3">
          <button
            onClick={() => window.location.href = 'https://paygent.transactiongateway.com/merchants/login.php'}
            className="px-4 py-2 text-[15px] font-medium text-gray-700 hover:text-blue-600 border border-gray-200 rounded-lg hover:border-blue-300 transition-all duration-200 whitespace-nowrap cursor-pointer"
          >
            Log in
          </button>
          <Link
            to="/signup"
            className="px-5 py-2 text-[15px] font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-all duration-200 whitespace-nowrap cursor-pointer"
            style={{ boxShadow: "0 4px 12px rgba(37,99,235,0.2)" }}
          >
            Get started
          </Link>
        </div>

        {/* Mobile hamburger */}
        <button
          className="md:hidden flex items-center justify-center w-10 h-10 rounded-lg hover:bg-gray-100 cursor-pointer"
          onClick={() => setMobileOpen(!mobileOpen)}
        >
          <i className={`${mobileOpen ? "ri-close-line" : "ri-menu-line"} text-xl w-6 h-6 flex items-center justify-center`} />
        </button>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="md:hidden bg-white border-t border-gray-100 px-6 py-4 space-y-2 h-[80vh] overflow-y-auto">
          {navItems.map((item) => (
            <div key={item.label} className="py-2">
              {item.href ? (
                <Link
                  to={item.href}
                  className="block text-[15px] font-medium text-gray-900 hover:text-blue-600 transition-colors"
                  onClick={() => setMobileOpen(false)}
                >
                  {item.label}
                </Link>
              ) : (
                <div className="text-[15px] font-bold text-gray-900 mb-2 border-b border-gray-100 pb-2">
                  {item.label}
                </div>
              )}
              {item.items && (
                <div className="pl-4 space-y-2 mt-2">
                  {item.items.map((sub) => (
                    <Link
                      key={sub.label}
                      to={sub.href}
                      className="block text-sm text-gray-600 hover:text-blue-600 transition-colors"
                      onClick={() => setMobileOpen(false)}
                    >
                      {sub.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          ))}
          <Link to="/tapn" className="flex items-center gap-2 py-2" onClick={() => setMobileOpen(false)}>
            <img src="/tapn-logo.png" alt="Tapn" className="h-8 w-auto object-contain" style={{ mixBlendMode: 'multiply' }} />
          </Link>
          <div className="pt-6 mt-4 border-t border-gray-100 flex flex-col gap-3 pb-8">
            <button onClick={() => window.location.href = 'https://paygent.transactiongateway.com/merchants/login.php'} className="py-3 text-center text-[15px] font-medium border border-gray-200 rounded-lg text-gray-700">Log in</button>
            <Link to="/signup" className="py-3 text-center text-[15px] font-semibold text-white bg-blue-600 rounded-lg" onClick={() => setMobileOpen(false)}>Get started</Link>
          </div>
        </div>
      )}
    </header>
  );
}
